import { GetAwayAvailability, GetAwayRate } from '~/core/ts/entity/GetAwayAvailability'
import GetAwayAPIRepository from '~/core/ts/repository/get-away/GetAwayAPIRepository'

export function useGetAwayAvailability() {
  const availabilityList = ref<Array<GetAwayAvailability>>([])

  const availabilityByGetAwayUUID = ref<Record<string, GetAwayAvailability>>({})

  const fetchedAvailabilityDateFrom = ref<Date | null>(null)
  const fetchedAvailabilityDateTo = ref<Date | null>(null)

  function getAvailabilityOf(getAwayUUID: string) {
    const cache = availabilityByGetAwayUUID.value[getAwayUUID]
    if (cache) {
      return cache
    }

    for (const availability of availabilityList.value) {
      if (availability.getAwayUUID === getAwayUUID) {
        return availability
      }
    }

    return null
  }

  function generateNextWeekEndFromAndToDates() {
    const todayWithNoTime = new Date()

    const dayIndex = todayWithNoTime.getDay()

    const dayDiff = (6 - dayIndex) + 14

    const saturdayStamp = todayWithNoTime.getTime() + (dayDiff) * 24 * 60 * 60 * 1000
    const from = new Date(saturdayStamp)

    const sundayStamp = todayWithNoTime.getTime() + (dayDiff+1) * 24 * 60 * 60 * 1000
    const to = new Date(sundayStamp)

    return { from, to }
  }

  async function getAvailabilityForWeekendByUUIDs(uuids: Array<string>) {
    const { from, to } = generateNextWeekEndFromAndToDates()

    fetchedAvailabilityDateFrom.value = from
    fetchedAvailabilityDateTo.value = to

    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      console.error(`Invalid dates for Availability fetch. From: ${from.getTime()}, to: ${to.getTime()}`)
      return
    }

    availabilityList.value = await GetAwayAPIRepository.getAvailabilityByUUIDs(uuids, from, to, 2)

    const availByUuid: Record<string, GetAwayAvailability> = {}
    availabilityList.value.forEach((avail) => availByUuid[avail.getAwayUUID] = avail)
    availabilityByGetAwayUUID.value = availByUuid
  }

  async function getAvailabilityByUUIDs(uuids: string[], from: Date, to: Date, pax: number) {
    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      console.error(`Invalid dates for Availability fetch. From: ${from.getTime()}, to: ${to.getTime()}`)
      return
    }

    try {
      availabilityList.value = await GetAwayAPIRepository.getAvailabilityByUUIDs(uuids, from, to, pax)
    } catch (error) {
      availabilityList.value = []
    }

    const availByUuid: Record<string, GetAwayAvailability> = {}
    availabilityList.value.forEach((avail) => availByUuid[avail.getAwayUUID] = avail)
    availabilityByGetAwayUUID.value = availByUuid

    fetchedAvailabilityDateFrom.value = from
    fetchedAvailabilityDateTo.value = to
  }

  async function getAvailabilityByLocationUUID(locationUUID: string, from: Date, to: Date, pax: number) {
    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      console.error(`Invalid dates for Availability fetch. From: ${from.getTime()}, to: ${to.getTime()}`)
      return
    }

    availabilityList.value = await GetAwayAPIRepository.getAvailabilityByLocation(locationUUID, from, to, pax)

    const availByUuid: Record<string, GetAwayAvailability> = {}
    availabilityList.value.forEach((avail) => availByUuid[avail.getAwayUUID] = avail)
    availabilityByGetAwayUUID.value = availByUuid

    fetchedAvailabilityDateFrom.value = from
    fetchedAvailabilityDateTo.value = to
  }

  function isValidAvailability(avail: GetAwayAvailability | null) {
    if (!avail) {
      return false
    }

    if (!avail.bestRate) {
      return false
    }

    return true
  }


  function ensureAvailability(avail: GetAwayAvailability | null): GetAwayAvailability {
    if (!isValidAvailability(avail)) {
      throw new Error('INVALID OPERATION!')
    }

    return avail as GetAwayAvailability
  }

  function ensureAvailabilityBestRate(avail: GetAwayAvailability | null): GetAwayRate {
    if (!isValidAvailability(avail)) {
      throw new Error('INVALID OPERATION!')
    }

    return ensureAvailability(avail).bestRate as GetAwayRate
  }

  return {
    availabilityList,

    fetchedAvailabilityDateFrom,
    fetchedAvailabilityDateTo,

    getAvailabilityByUUIDs,
    getAvailabilityByLocationUUID,
    getAvailabilityForWeekendByUUIDs,

    // Util
    isValidAvailability,
    ensureAvailability,
    ensureAvailabilityBestRate,

    getAvailabilityOf,

    generateNextWeekEndFromAndToDates,
  }
}
